/**
 * The search shelf's buy surface — pure preparation, no React, no DOM.
 *
 * Every card ends on ONE button, the same button: the ink "Buy Now" pill
 * naming the store it opens, pinned to the card's foot. What differs is where
 * it goes and what sits beside the price. A card whose market has two or more
 * offers to compare gets the product page's pick behind the button and a
 * "N stores" toggle on its price line; a card with one direct link gets that
 * link and no toggle. That is the product page's STRUCTURAL rule
 * (external-link.tsx) — the comparison exists only from two surviving rows —
 * with no flag, because one offer has nothing to compare against.
 *
 * Every href built here leaves through the /go hop (buildOutboundHref), the
 * same first-party redirect the product page's CTA takes. Until 2026-09 the
 * shelf linked straight at the UNTAGGED Amazon URL `buildDirectRetailerUrl`
 * hands it — the tag is stripped there so no machine surface ever carries one,
 * and nothing put it back — so a human click from the shelf was a click that
 * could never earn a commission and that no traffic_events row ever saw.
 *
 * A product this market does not buy at retail (purchase-channel.ts: a
 * prescription hearing aid, or a product not sold here) is the one exception
 * to "every card ends on the button": its card opens only its one real sale,
 * a priced offer at a store in this market, or says where it is sold instead.
 * Never an Amazon listing or search, the verdict its product card follows.
 */

import { amazonSearchForProduct } from '@/lib/amazon-search-query';
import type { LocaleDetails } from 'shared/src/types/languages.type';
import type { SearchProductPreview } from 'shared/src/types/search.type';
import { getAmazonDomainForRegion } from 'shared/src/types/amazon.type';
import { buildOutboundHref } from 'shared/src/utils/outbound-link';
import { getRetailerDisplayName } from 'shared/src/utils/retailer-name';
import { isAmazonUrl } from '@/lib/native/amazon-app-policy';
import {
  countOfferRows,
  isAmazonOffer,
  isInfoOffer,
  normalizeMerchantDomain,
  offerObservedAt,
  offerOutboundContext,
  preferAmazonWhenOutOfStock,
  prepareOffers,
  type OfferKind,
  type PreparedOffer,
  type PreparedOffers,
} from '@/components/products/links/offers/offers';
import { isLabelIneligibleKind } from 'shared/src/utils/offers/rank-offers';
import { purchaseChannel } from 'shared/src/utils/purchase-channel';

export type ShelfCardPrice = {
  amount: number;
  /** The pick's was-price when it is showing a real discount. */
  originalAmount?: number;
  /** Whose number this is: the recommended offer's, or the stored listing price.
   *  The card's price line has to name the price of what its button opens —
   *  the same rule PriceInfo follows under the product page's pill. */
  source: 'offer-pick' | 'listing';
  /** Set when the price is Amazon's: when the Creators API read it. An Amazon
   *  price with no date is not shown (Associates IP License 2(c)(i)). */
  amazonObservedAt?: string;
  /** The honesty badge when the pick earns it (spec 6.2, 6.6.2): "{n}% below
   *  its usual price" on the price line, for the pick only, with the usual
   *  price and the days of data its tooltip names. */
  belowUsual?: {
    percent: number;
    typicalPriceLabel: string;
    typicalPricePoints?: number;
  };
};

/** The one button every card ends on. */
export type ShelfCardButton = {
  /** The /go href. */
  href: string;
  isAmazon: boolean;
  /** 'search' when the destination is a search results page rather than a
   *  listing — the label says so ("Search Now"). See `OfferKind`. */
  kind: OfferKind;
  /** "Amazon", or the store's display name — printed under "Buy Now". */
  merchantName: string;
  /** The store's host, lower-cased and `www.`-stripped: the favicon lookup
   *  key for a non-Amazon store, and the `merchant_domain` click dimension. */
  merchantDomain: string;
  /** Present when the button opens a ranked offer (the dock's pick): its
   *  position in the list, so `offer_index` stays comparable with the rows. */
  offerIndex?: number;
};

export type ShelfCardDock = {
  prepared: PreparedOffers;
  /** `prepareOffers().primary` — what the button opens. Usually one of the
   *  rows, badged "Our pick", so its price sits beside the others'; when every
   *  store is out of stock it is the synthesized Amazon fallback instead
   *  (`preferAmazonWhenOutOfStock`), which is NOT in the rows. */
  primary: PreparedOffer;
  /** The cheapest firm, in-stock row when it undercuts the pick — the honest
   *  reason to open the list, printed as "From {price}" under the toggle.
   *  Null when the pick already IS the cheapest or nothing firm undercuts it. */
  cheaperThanPick: PreparedOffer | null;
};

export type ShelfCardOffers = {
  /** Non-null exactly when the card has offers to compare. */
  dock: ShelfCardDock | null;
  /** The button — the pick when there is a dock, else the direct link. Null
   *  when there is nothing to link (no URL, or Amazon inside the app shell). */
  button: ShelfCardButton | null;
  /** What the card's price line shows, or null when nothing is priced. */
  price: ShelfCardPrice | null;
  /** Wraps an offer's untagged URL in the /go hop. */
  hrefFor: (offer: PreparedOffer) => string;
  /** Set when this market does not buy the product at retail (shelfCardChannel):
   *  with no button, the card's foot says where it is sold instead. */
  channel: 'clinic' | 'not-in-market' | null;
};

export type ShelfCardOffersInput = {
  product: SearchProductPreview;
  locale: LocaleDetails;
  /** The app-shell Associates gate — see lib/native/amazon-app-policy.ts.
   *  Passed INTO prepareOffers rather than applied afterwards, so a product
   *  whose only other offer is Amazon drops back to the single link instead
   *  of rendering a one-row "2 stores" dock. */
  suppressAmazon: boolean;
  /** The page the click comes from, recorded on the /go event. */
  path?: string | null;
  /** The card's rank in the strip — the `product_index` click dimension. */
  index: number;
};

/**
 * The /go href for one destination. A ranked row lends its opaque tracking
 * token and its id (`t=` and `o=`, spec 5.3.2), under the product page's
 * rule: an information link and an Amazon row carry no token
 * (`offerOutboundContext`). A direct link has neither.
 */
const outboundHref = (
  url: string,
  locale: LocaleDetails,
  path: string | null | undefined,
  index: number,
  offer?: PreparedOffer,
): string =>
  buildOutboundHref(url, {
    region: locale.region,
    path: path ?? undefined,
    index,
    ...(offer ? offerOutboundContext(offer) : {}),
  });

/**
 * The card's own price. When the card's link is an Amazon listing the price
 * is Amazon's, and it shows only with the time Amazon's API gave it
 * (projectSearchProductPreview sets it from the listing's current Creators row).
 */
const listingPrice = (product: SearchProductPreview): ShelfCardPrice | null => {
  if (!product.price || !(product.price.amount > 0)) return null;
  const direct = product.directRetailerUrl;
  const amazonListing = !!direct && isAmazonUrl(direct) && !isAmazonSearchUrl(direct);
  const observedAt = product.price.amazonObservedAt;
  if (amazonListing && !observedAt) return null;
  return {
    amount: product.price.amount,
    source: 'listing',
    ...(amazonListing && observedAt ? { amazonObservedAt: observedAt } : {}),
  };
};

const pickPrice = (primary: PreparedOffer): ShelfCardPrice | null => {
  // A list, member or hidden price is not the price a shopper pays, so the
  // line never quotes it as the pick's (spec 6.5); the listing price answers.
  if (isInfoOffer(primary) || isLabelIneligibleKind(primary)) return null;
  const amount = primary.price?.value;
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) return null;
  // `originalPriceLabel` is '' when prepareOffers suppressed the was-price
  // against the typical price (spec 6.2.5), so the line follows the same rule.
  const original = primary.originalPriceLabel ? primary.originalPrice?.value : undefined;
  const amazonObservedAt = isAmazonOffer(primary) ? offerObservedAt(primary) : null;
  // An Amazon price nobody can date (a synthesized fallback) is not shown.
  if (isAmazonOffer(primary) && !amazonObservedAt) return null;
  return {
    amount,
    ...(typeof original === 'number' && Number.isFinite(original) && original > amount
      ? { originalAmount: original }
      : {}),
    source: 'offer-pick',
    ...(amazonObservedAt ? { amazonObservedAt } : {}),
    ...(typeof primary.belowUsualPercent === 'number'
      ? {
          belowUsual: {
            percent: primary.belowUsualPercent,
            typicalPriceLabel: primary.typicalPriceLabel,
            ...(typeof primary.typicalPricePoints === 'number'
              ? { typicalPricePoints: primary.typicalPricePoints }
              : {}),
          },
        }
      : {}),
  };
};

/**
 * The cheapest row a shopper could actually act on that beats the pick.
 *
 * Firm prices only: a hedged "~$129" row is not a number to print "From" in
 * front of, an out-of-stock row is not a place to buy, and a list, member or
 * hidden price is not a price a shopper pays. A search row or an information
 * link has no price at all and never qualifies.
 */
export const findCheaperThanPick = (
  rows: readonly PreparedOffer[],
  primary: PreparedOffer,
): PreparedOffer | null => {
  const pickAmount = primary.price?.value;
  if (typeof pickAmount !== 'number' || !Number.isFinite(pickAmount)) return null;

  let cheapest: PreparedOffer | null = null;
  for (const row of rows) {
    if (row === primary || row.kind === 'search' || isInfoOffer(row)) continue;
    if (isLabelIneligibleKind(row)) continue;
    if (row.availability === 'out_of_stock' || row.isApproximate) continue;
    const amount = row.price?.value;
    if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) continue;
    if (amount >= pickAmount) continue;
    if (!cheapest || amount < cheapest.price!.value) cheapest = row;
  }
  return cheapest;
};

const pickButton = (
  dock: ShelfCardDock,
  locale: LocaleDetails,
  path: string | null | undefined,
  index: number,
): ShelfCardButton => {
  const { primary, prepared } = dock;
  const position = prepared.rows.indexOf(primary);
  return {
    href: outboundHref(primary.url, locale, path, index, primary),
    isAmazon: isAmazonOffer(primary),
    kind: primary.kind ?? 'offer',
    merchantName: primary.merchantName?.trim() || primary.domainKey,
    merchantDomain: primary.domainKey,
    // A synthesized fallback has no position in the list.
    ...(position >= 0 ? { offerIndex: position } : {}),
  };
};

/** An Amazon URL that is a search results page, not a listing. */
const isAmazonSearchUrl = (url: string): boolean => isAmazonUrl(url) && /\/s\?|[?&]k=/.test(url);

/**
 * Where the button goes when every store in the list is out of stock: the
 * product's own Amazon listing when its direct link is one, else an Amazon
 * search for it in this market — the same search the product page falls back
 * to when it has no link at all. Untagged; /go tags for humans.
 */
const amazonFallbackFor = (
  product: SearchProductPreview,
  locale: LocaleDetails,
): { amazonUrl?: string; amazonSearchUrl: string } => {
  const direct = product.directRetailerUrl;
  const directIsAmazon = !!direct && isAmazonUrl(direct);
  const directIsSearch = !!direct && isAmazonSearchUrl(direct);
  return {
    ...(direct && directIsAmazon && !directIsSearch ? { amazonUrl: direct } : {}),
    amazonSearchUrl:
      direct && directIsSearch
        ? amazonSearchForProduct(direct, { title: product.title, brandName: product.brand })
        : `https://www.${getAmazonDomainForRegion(locale.regionCode)}/s?k=${encodeURIComponent(product.title)}`,
  };
};

const directLinkButton = (
  product: SearchProductPreview,
  locale: LocaleDetails,
  suppressAmazon: boolean,
  path: string | null | undefined,
  index: number,
): ShelfCardButton | null => {
  const stored = product.directRetailerUrl;
  if (!stored) return null;
  // A cached preview can still carry a stored search naming another product.
  const url = amazonSearchForProduct(stored, { title: product.title, brandName: product.brand });
  const isAmazon = isAmazonUrl(url);
  // The Associates gate: an Amazon destination is withheld inside the app
  // shell, exactly as the product page withholds its CTA there.
  if (isAmazon && suppressAmazon) return null;
  return {
    href: outboundHref(url, locale, path, index),
    isAmazon,
    kind: isAmazonSearchUrl(url) ? 'search' : 'offer',
    merchantName: isAmazon ? 'Amazon' : getRetailerDisplayName(url),
    merchantDomain: normalizeMerchantDomain(url),
  };
};

/**
 * The purchase-channel verdict of a shelf card (purchase-channel.ts): the one
 * its preview carries, stamped from the full product by
 * projectSearchProductPreview, or for a preview projected before the stamp
 * existed, the same verdict read from the preview's own name and brand. Null
 * for a product sold at retail.
 */
export const shelfCardChannel = (
  product: SearchProductPreview,
  locale: LocaleDetails,
): 'clinic' | 'not-in-market' | null => {
  if (product.purchaseChannel) return product.purchaseChannel;
  const channel = purchaseChannel({ title: product.title, brandName: product.brand }, { regionCode: locale.regionCode });
  return channel === 'clinic' || channel === 'not-in-market' ? channel : null;
};

/**
 * Decide, for one card, what its button opens, whether it has a list of
 * stores to compare, and what its price line shows.
 */
export function prepareShelfCardOffers({
  product,
  locale,
  suppressAmazon,
  path,
  index,
}: ShelfCardOffersInput): ShelfCardOffers {
  const hrefFor = (offer: PreparedOffer): string =>
    outboundHref(offer.url, locale, path, index, offer);

  // Not sold at retail here: the one real sale the projection kept (a
  // store's priced offer in `directRetailerUrl`, with its price), or nothing.
  // No list of stores and no Amazon fallback. A preview without the stamp
  // cannot say which of its links is a sale, so it offers none.
  const channel = shelfCardChannel(product, locale);
  if (channel) {
    const sale = product.purchaseChannel ? product.directRetailerUrl : null;
    const button =
      sale && !isAmazonUrl(sale) ? directLinkButton(product, locale, suppressAmazon, path, index) : null;
    return { dock: null, button, price: button ? listingPrice(product) : null, hrefFor, channel };
  }

  // The product page's pipeline, then the product page's out-of-stock rule:
  // a dead pick hands the button to Amazon (a listing or a search) while the
  // rows keep saying what we observed.
  const prepared = preferAmazonWhenOutOfStock(
    prepareOffers(product.offers, {
      regionCode: locale.regionCode,
      localePath: locale.path,
      suppressAmazon,
    }),
    {
      ...amazonFallbackFor(product, locale),
      listingPrice: product.price?.amount,
      listingObservedAt: product.price?.amazonObservedAt,
      regionCode: locale.regionCode,
      localePath: locale.path,
      suppressAmazon,
    },
  );
  // Two rows a reader can act on; information links never count (spec 6.4.5,
  // 6.6.1), exactly as on the product page.
  const primary = countOfferRows(prepared.rows) >= 2 ? prepared.primary : null;
  const dock: ShelfCardDock | null = primary
    ? { prepared, primary, cheaperThanPick: findCheaperThanPick(prepared.rows, primary) }
    : null;

  // The line under the button is the price of what the button opens. A pick
  // without a price (a partner row we never priced) falls back to the listing.
  const price = dock ? (pickPrice(dock.primary) ?? listingPrice(product)) : listingPrice(product);

  return {
    dock,
    button: dock
      ? pickButton(dock, locale, path, index)
      : directLinkButton(product, locale, suppressAmazon, path, index),
    price,
    hrefFor,
    channel: null,
  };
}

/**
 * The amount a card's price line will show — what the shelf's in-budget
 * summary has to count, or the header would say "3 / 5 under $300" about
 * numbers the cards themselves do not print.
 */
export function shelfCardDisplayPrice(
  product: SearchProductPreview,
  locale: LocaleDetails,
  suppressAmazon: boolean,
): number | null {
  return prepareShelfCardOffers({ product, locale, suppressAmazon, index: 0 }).price?.amount ?? null;
}
